import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SidebarProvider, useSidebar } from '../context/SidebarContext';
import { HOTEL_BASE, HOTEL_PROFILE_PATH } from '../design-v2/hotel/model';
import {
  experienceLanding,
  isLanding,
  lastExperience,
  rememberExperience,
  useExperiences,
} from '../design-v2/hotel/shell/experiences';
import AppFrameV2 from './AppFrameV2';
import AppHeader from './AppHeader';
import AppSidebar from './AppSidebar';
import Backdrop from './Backdrop';
import { useV2Frame } from './v2Frame';

const LayoutContent: React.FC = () => {
  const { isExpanded, isHovered, isMobileOpen } = useSidebar();

  return (
    <div className="app-shell min-h-screen xl:flex">
      <div>
        <AppSidebar />
        <Backdrop />
      </div>
      <div
        className={`flex min-h-screen flex-1 flex-col transition-all duration-300 ease-in-out ${
          isExpanded || isHovered ? 'lg:ml-[250px]' : 'lg:ml-[84px]'
        } ${isMobileOpen ? 'ml-0' : ''}`}
      >
        <AppHeader />
        <div className="mx-auto w-full max-w-[1520px] flex-1 px-3 pb-5 pt-3 md:px-4 md:pb-7 md:pt-4 xl:px-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

/**
 * Where the restaurant shell sends this user instead of rendering, if
 * anywhere (IA §2 hotel-only guard, Q2 landing). `undefined` while that is
 * still unknown and might be the hotel; `null` to render the restaurant.
 */
function useRestaurantRedirect(): string | null | undefined {
  const { accessToken, user, loading } = useAuth();
  const { pathname, key } = useLocation();
  const { hasRestaurant, hasHotel, pending } = useExperiences();

  useEffect(() => {
    if (user && !pending && hasRestaurant) {
      rememberExperience(user.id, 'restaurant');
    }
  }, [user, pending, hasRestaurant]);

  if (pending) {
    // Only wait when the answer could be "hotel": a signed-in user whose
    // user-info hasn't arrived, a cached user saved without the group field
    // while user-info is in flight, or a group whose property list hasn't
    // answered. A user cached outside every group renders now, as before
    // (`loading`, not `userInfoSettled`: the legacy endpoint never settles);
    // signed-out users fall through to ProtectedRoute's redirect.
    const mayBeHotel = user
      ? user.store_group_id != null ||
        (user.store_group_id === undefined && loading)
      : accessToken !== null;
    return mayBeHotel ? undefined : null;
  }
  if (!user) return null;
  if (hasHotel && !hasRestaurant) {
    return pathname === '/profile' ? HOTEL_PROFILE_PATH : HOTEL_BASE;
  }
  // Dual mode (IA Q2 (b)): the first `/` of a page load or a sign-in opens
  // the experience last used; restaurant by default. Never a later `/`.
  if (
    hasHotel &&
    isLanding(user.id, key) &&
    pathname === '/' &&
    lastExperience(user.id) === 'hotel'
  ) {
    return experienceLanding('hotel', user.id);
  }
  return null;
}

/**
 * The restaurant shell (`RestaurantShell` in the hotel IA): the frame around
 * every protected restaurant route. `AppFrameV2` takes over when the
 * V2_FRAME flag is on (see `v2Frame.ts`); the legacy shell is retired once
 * the flip is permanent (migration audit E1.10).
 *
 * Hotel pages never render here: `/hotel/*` is a sibling layout route under
 * `HotelShell`. A hotel-only user is sent there instead, and nothing of the
 * restaurant frame renders while that is being decided.
 */
const AppLayout: React.FC = () => {
  const v2 = useV2Frame();
  const redirect = useRestaurantRedirect();
  if (redirect === undefined) {
    return (
      <div
        aria-busy="true"
        className="flex min-h-screen items-center justify-center text-gray-900 dark:text-white"
      >
        Loading...
      </div>
    );
  }
  if (redirect !== null) return <Navigate to={redirect} replace />;
  if (v2) return <AppFrameV2 />;
  return (
    <SidebarProvider>
      <LayoutContent />
    </SidebarProvider>
  );
};

export default AppLayout;
